/**
 * ./src/exercise1/Exercise1Workout.js
 * 
 * This module guides users through the workout phase of Exercise 1.
 * It features **breathing cycles, animated visual cues, a progress bar, and tracking**.
 * 
 * - **Controlled Breathing Cycles:**
 *   - Breathing **pattern animation** with inhale, hold, exhale, and pause phases.
 *   - Uses **animated circles & a person guide** for real-time breathing synchronization.
 *
 * - **Breathing Timing & Progress Tracking:**
 *   - Users select a **workout duration**. 
 *   - Progress bar visually **tracks remaining workout time**.
 *   - Tracks **total time spent** and **transfers data to Exercise1Finish.js**.
 *
 * - **User Controls & Navigation:**
 *   - Play/Pause buttons for **starting & stopping breathing cycles**.
 *   - "Edit Rhythm" button allows users to **fine-tune their breathing timing**.
 *   - Users can **exit the workout early** and return to the main menu.
 *   - Automatically **navigates to Exercise1Finish.js** upon completion.
 * 
 */


import React, { useState, useEffect, useRef } from 'react';
import './Exercise1Workout.css'; // Importing the CSS file for styling
import Circle1 from '../components/icons/circlein1.svg';
import Circle2 from '../components/icons/circlein2.svg';
import Circle3 from '../components/icons/circlein3.svg';
import Circle4 from '../components/icons/circlein4.svg';
import Circle5 from '../components/icons/circlein5.svg';
import Circle6 from '../components/icons/circlein6.svg';
import Circle7 from '../components/icons/circlein7.svg';

import stick1 from "../components/icons/stick1.svg";
import stick2 from "../components/icons/stick2.svg";
import stick3 from "../components/icons/stick3.svg";
import stick4 from "../components/icons/stick4.svg";
import stick5 from "../components/icons/stick5.svg";
import stick6 from "../components/icons/stick6.svg";
import stick7 from "../components/icons/stick7.svg";

import person1 from '../components/icons/person1.svg';
import person2 from '../components/icons/person2.svg';
import person3 from '../components/icons/person3.svg';
import person4 from '../components/icons/person4.svg';
import person5 from '../components/icons/person5.svg';
import person6 from '../components/icons/person6.svg';
import person7 from '../components/icons/person7.svg';

import CloseIcon from '../components/icons/close.svg'; 
import InfoIcon from '../components/icons/InfoIcon.svg'; 
import PlayIcon from '../components/icons/play.svg';
import StopIcon from '../components/icons/stop.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import Curtain from '../components/Curtain'; // Import the Curtain component
import { DEBUG_TIME } from "../config";

function Exercise1Workout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [long, setLong] = useState(2.0);
  const [short, setShort] = useState(1.0);
  const [hold, setHold] = useState(1.5);
  const [isPlaying, setIsPlaying] = useState(false);
  const timeRef = useRef(0); // Add this to keep track of actual time
  const [isTransitioningToOne, setIsTransitioningToOne] = useState(false);

  useEffect(() => {
    // Load values from localStorage
    setLong(parseFloat(localStorage.getItem('long')) || 2.0);
    setShort(parseFloat(localStorage.getItem('short')) || 1.0);
    setHold(parseFloat(localStorage.getItem('hold')) || 1.5);
  }, []);

  const duration = location.state?.duration || 3; // Get duration from navigation state
  const isWarmup = location.state?.isWarmup || false;
  const exerciseNumber = location.state?.exerciseNumber;
  console.log(isWarmup)
  console.log(exerciseNumber)
  const [time, setTime] = useState(() => DEBUG_TIME ?? location.state?.time ?? duration * 60); // Convert minutes to seconds
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1); // Track the current circle step
  const [nextStep, setNextStep] = useState(2); // Track the next circle step
  const [previousStep, setPreviousStep] = useState(null);
  const [fadeInStyle, setFadeInStyle] = useState({ opacity: 1 }); // Style for fade-in effect
  const [fadeOutStyle, setFadeOutStyle] = useState({ opacity: 0 }); // Style for fade-out effect
  const [showCurtain, setShowCurtain] = useState(false);
  const [instructionText, setInstructionText] = useState('');
  const [showPlayIcon, setShowPlayIcon] = useState(true); // Add this new state
  const [showStopIcon, setShowStopIcon] = useState(false);

  useEffect(() => {
    let interval;
    if (isPlaying && time > 0) {
      interval = setInterval(() => {
        setTime(prevTime => {
          const newTime = prevTime - 0.1;
          setProgress(100 - (newTime / (duration * 60)) * 100); // Update calculation
          return newTime > 0 ? newTime : 0;
        });
      }, 100);
    } else if (time === 0) {
      setIsPlaying(false);
    }
    return () => clearInterval(interval);
  }, [isPlaying, time, duration]);

  useEffect(() => {
    let timeout;
    if (isPlaying) {
      const steps = [
        [stick1, stick1], // Pair of sticks for Step 1
        [stick2, stick2], // Pair of sticks for Step 2
        [stick3, stick3],
        [stick4, stick4],
        [stick5, stick5],
        [stick6, stick6],
        [stick7, stick7],
      ];
      const durations = [long, long, long, short, short, short, short];
      
      const fadeInNextCircle = () => {
        timeout = setTimeout(() => {
          setCurrentStep(nextStep);
          
          // Update instruction text
          if (currentStep == 2||currentStep == 3) {
            setInstructionText('Inhale');
          } else if (currentStep == 4) {
            setInstructionText('Hold');
          } else if (currentStep >=5&&currentStep<=7) {
            setInstructionText('Exhale');
          } else {
            setInstructionText('Hold');
          }

          if (nextStep === 1) {
            setIsTransitioningToOne(true);
            setFadeInStyle({ opacity: 1, transition: `opacity 0.2s ease-in` });
            setFadeOutStyle({ opacity: 0, transition: `opacity 0.2s ease-out` });
          } else {
            setIsTransitioningToOne(false);
            setFadeInStyle({ opacity: 1, transition: `opacity ${durations[nextStep - 1]}s ease-in` });
          }

          timeout = setTimeout(() => {
            setPreviousStep(null);

            timeout = setTimeout(() => {
              setNextStep((prev) => (prev === 7 ? 1 : prev + 1));
              setPreviousStep(currentStep);
              fadeInNextCircle();
            }, hold * 1000);
          }, nextStep === 1 ? 200 : durations[nextStep - 1] * 1000);
        }, 10);
      };

      fadeInNextCircle();
    }

    return () => clearTimeout(timeout);
  }, [isPlaying, nextStep, long, short, hold, currentStep]);

  useEffect(() => {
    if (time <= 0) {
      setIsPlaying(false);
      setShowCurtain(true);
      console.log("isWarmup");
      console.log(isWarmup);
      console.log(exerciseNumber)


      
      if (isWarmup) {
        setTimeout(() => {
          navigate('/Warmup2', {
            state: {
              exerciseNumber: exerciseNumber,
                    isWarmup: true,
                    originalReturnTo: location.state?.originalReturnTo
            }
          });
        }, 2000);
      } else {
        setTimeout(() => {
          navigate('/exercise1finish', {
            state: {
              timeSpent: timeRef.current,
              duration: duration
            }
          });
        }, 2000);
      }
    }
}, [time, navigate, isWarmup]);
// hi

  useEffect(() => {
    let timer;
    if (isPlaying) {
      timer = setInterval(() => {
        timeRef.current += 1;
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  const startBreathing = () => {
    setIsPlaying(true);
    setNextStep(2); // Start with Circle 2 after Circle 1
    setFadeInStyle({ opacity: 1 }); // Keep Circle 1 visible
    setShowPlayIcon(false);
    setShowStopIcon(false);
  };

  const stopBreathing = () => {
    setIsPlaying(false);
    setShowStopIcon(true);
    setInstructionText('Pause');
    setCurrentStep(1);
    setNextStep(2);
    setPreviousStep(null);
    setFadeOutStyle({ opacity: 0 });
    setFadeInStyle({ opacity: 1 });
  };

  const resumeBreathing = () => {
    setIsPlaying(true);
    setShowStopIcon(false);
    // activeTime will resume automatically due to the useEffect
  };

  const handleEdit = () => {
    // check if the user has encountered /RhythmChoosing before
    const hasCompletedRhythmChoosing = localStorage.getItem("hasCompletedRhythmChoosing") === "true";

    navigate(hasCompletedRhythmChoosing ? '/RhythmChoosingNoPopup' : '/RhythmChoosing', { 
      state: { 
        returnTo: '/exercise1workout',
        duration: duration, // Pass duration to choosing page
        time: time // Pass current time into choosing page

      } 
    });
  };

  const handleClose = () => {
    navigate('/'); // Navigate to the landing page
  };

  const handleGuidance = () => {
    setIsPlaying(false);  // Pause the exercise
    setShowStopIcon(true);  // Show stop icon when returning
    setInstructionText('Pause');
    navigate('/exercise1video');
  };

  return (
    <div className="exercise-container">
      {showCurtain && (
        <Curtain 
          appear={true}
          duration={2000} 
        />
      )}
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img 
            src={CloseIcon} 
            alt="close icon" 
            className="close-icon" 
            onClick={handleClose}
          />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
          <span>{`${Math.floor(time / 60)}:${String(Math.floor(time % 60)).padStart(2, '0')}`}</span>
        </div>

        <div className="button-container">
          <button className="save-button" onClick={handleEdit}>Edit Rhythm</button>
        </div>
      </div>

      {/* Circle control and breathing inputs */}
      <div className="main-content">
        <div className="circle-container">
          <div className="circle-wrapper">
            {[
              [stick1, stick1],
              [stick2, stick2],
              [stick3, stick3],
              [stick4, stick4],
              [stick5, stick5],
              [stick6, stick6],
              [stick7, stick7],
            ].map((StickPair, index) => (
              <div key={index} className="stick-container">
                {/* Left Stick */}
              <img
                src={StickPair[0]}
                alt={`stick left step ${index + 1}`}
                className="breathing-stick left-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
                {/* Right Stick */}
              <img
                src={StickPair[1]}
                alt={`stick right step ${index + 1}`}
                className="breathing-stick right-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
              </div>
            ))}
            
            {[person1, person2, person3, person4, person5, person6, person7].map((PersonImg, index) => (
              <img
                key={`person-${index}`}
                src={PersonImg}
                alt={`person step ${index + 1}`}
                className="breathing-circle"
                style={{
                  position: 'absolute',
                  height: '55%',
                  width: 'auto',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  ...(currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 || (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne ? fadeOutStyle : { opacity: 1 }
                    : { opacity: 0 }),
                  cursor: 'pointer',
                  pointerEvents: currentStep === index + 1 ? 'auto' : 'none'
                }}
                onClick={() => {
                  console.log('Person clicked', index + 1);
                  if (currentStep === index + 1) {
                    stopBreathing();
                  }
                }}
              />
            ))}
            {showPlayIcon && (
              <img
                src={PlayIcon}
                alt="play icon"
                className="play-icon"
                onClick={startBreathing}
              />
            )}
            {showStopIcon && (
              <img
                src={StopIcon}
                alt="stop icon"
                className="play-icon"
                style={{ opacity: 1 }}
                onClick={resumeBreathing}
              />
            )}
          </div>
        </div>

        {/* Control buttons below the circle */}
        <div className="breathing-controls">
          <button className="guidance-video-button" onClick={handleGuidance}>Guidance Video</button>
          <div className="control">
            <div className='row2'>
              <span>{instructionText}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        Repeat the rhythmic breathing cycle. If you feel unnatual, please press "Edit Rhythm" to adjust.
      </div>

      {/* <div className="debug-info">
        Active Time: {activeTime}s
        <button onClick={handleManualTrack}>Manual Track</button>
      </div> */}
      
      {/* Add breathing times display
      <div className="breathing-times">
        <div className="time-item">
          <span className="time-label">Long:</span>
          <span className="time-value">{long.toFixed(1)}s</span>
        </div>
        <div className="time-item">
          <span className="time-label">Short:</span>
          <span className="time-value">{short.toFixed(1)}s</span>
        </div>
        <div className="time-item">
          <span className="time-label">Hold:</span>
          <span className="time-value">{hold.toFixed(1)}s</span>
        </div>
      </div> */}
    </div>
  );
}

export default Exercise1Workout;